import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { searchOdoo } from '@adornis/odoo/search.js';
import { Comment } from './comment.js';
import { ContactEntity } from './contact.js';
import { IntercomEntity } from './intercom.js';

/**
 * @entity MailAttachmentHeaderEntity
 */
@Entity()
export class MailAttachmentHeaderEntity extends AdornisEntity {
  static override _class = 'MailAttachmentHeaderEntity';

  @Field(() => ID) _id!: string;
  @Field(() => String) name: Maybe<string>;
  @Field(() => String) value: Maybe<string>;
}

/**
 * @entity MailAttachmentEntity
 */
@Entity()
export class MailAttachmentEntity extends AdornisEntity {
  static override _class = 'MailAttachmentEntity';

  @Field(() => ID) _id!: string;
  @Field(() => String) content: Maybe<string>;
  @Field(() => String) contentID: Maybe<string>;
  @Field(() => String) contentType: Maybe<string>;
  @Field(() => String) fileName: Maybe<string>;
  @Field(() => Boolean) isInline: Maybe<boolean>;
  @Field(() => [MailAttachmentHeaderEntity]) headers: Maybe<MailAttachmentHeaderEntity[]>;
}

/**
 * @entity OdooMessageEntity
 */
@Entity()
export class OdooMessageEntity extends AdornisEntity {
  static override _class = 'OdooMessageEntity';

  @Field(() => ID) _id!: string;
  @Field(() => String) emailFrom: Maybe<string>;
  @Field(() => String) odooMailMessageId: Maybe<string>;
  @Field(() => String) body: Maybe<string>;
  @Field(() => Date) odooInsertedAt: Maybe<Date>;
  @Field(() => Float) rating: Maybe<number>; // only relevant for user type bot
  @Field(() => String) ratingText: Maybe<string>; // only relevant for user type bot
  @Field(() => String) odooMessageType: Maybe<string>; // CLIENT, BOT, SOURCE, ODOO
  static override resolveID(entity: OdooMessageEntity) {
    return entity._id;
  }
}

@Entity()
export class OdooEntity extends MongoEntity {
  static override _class = 'OdooEntity';
  static override _collectionName = 'Odoo';

  @Field(() => String) odooTicketID: Maybe<string>;
  @Field(() => String) subject: Maybe<string>;
  @Field(() => String) body: Maybe<string>;
  @Field(() => String) from: Maybe<string>;
  @Field(() => Date) createdAt: Maybe<Date>;
  @Field(() => [MailAttachmentEntity]) attachments: Maybe<MailAttachmentEntity[]>;
  @Field(() => [OdooMessageEntity]) messages: Maybe<OdooMessageEntity[]>;
  @Field(() => [String]) odooTags: Maybe<string[]>;
  @Field(() => String) team: Maybe<string>;
  @Field(() => String) answer: Maybe<string>;
  @Field(() => Float) priority: Maybe<number>;
  @Field(() => Boolean) isHistoricalOrIntegrationTest: Maybe<boolean>;
  @Field(() => Boolean) integrationTestKey: Maybe<boolean>;
  @Field(() => [Comment]) comments: Maybe<Comment[]>; // Array to store user comments
  @Field(() => Boolean) isCheckedBySupport: Maybe<boolean>;
  @Field(() => Boolean) isOdooActive: Maybe<boolean>;
  @Field(() => Date) odooCreateDate: Maybe<Date>;

  static override async beforeCreate(entity: OdooEntity) {
    await super.beforeCreate(entity);
    entity.createdAt = new Date();
  }
}

export async function getOdooEntityByOdooTicketId(odooTicketId: string): Promise<OdooEntity | null> {
  const collection = await getCollection<OdooEntity>(OdooEntity._class);
  const odooEntity = await collection.findOne({ odooTicketID: odooTicketId });
  return odooEntity ? new OdooEntity(odooEntity) : null;
}

export async function getAllOdooTickets() {
  const collection = await getCollection<OdooEntity>(OdooEntity._class);
  const tickets = await collection.find({}).toArray();
  return tickets.map(ticket => new OdooEntity(ticket));
}

export async function getAllOdooTicketIdsMissingOdooCreateDate() {
  const collection = await getCollection<OdooEntity>(OdooEntity._class);
  const ticketsWithoutOdooCreateDateOrWithNullOdooCreateDate = await collection
    .find({
      $or: [{ odooCreateDate: { $exists: false } }, { odooCreateDate: null }],
    })
    .toArray();
  return ticketsWithoutOdooCreateDateOrWithNullOdooCreateDate.map(ticket => ticket.odooTicketID);
}

export async function setOdooPriorityByTicketId(priorityNr: number, ticketId: string) {
  const collection = await getCollection<OdooEntity>(OdooEntity._class);
  await collection.updateOne({ odooTicketID: ticketId }, { $set: { priority: priorityNr } });
}

export const getOdooTicketByTicketID = registerQuery({
  operationName: 'getOdooTicketByTicketID',
  type: () => OdooEntity,
  params: [{ name: 'ticketId', type: () => String }],
  resolve:
    (ticketId: string) =>
    async <SelSet extends BaseQLSelectionSet<OdooEntity>>(gqlFields: SelSet) => {
      const collection = await getCollection<OdooEntity>(OdooEntity._class);
      const mailEntity = await collection.findOne({ odooTicketID: ticketId });
      return mailEntity as Maybe<NarrowedEntity<OdooEntity, SelSet>>;
    },
});

export async function queryAndSaveOdooMessages(odooTicketId: string): Promise<void> {
  const collection = await getCollection<OdooEntity>(OdooEntity._class);
  const ticketIdNumber = parseInt(odooTicketId);

  const foundOdooTicket = await collection.findOne({ odooTicketID: odooTicketId });
  if (!foundOdooTicket) {
    logger.warn(`No Odoo ticket found with ID: ${odooTicketId}`);
    return;
  }

  let children = foundOdooTicket.messages || [];

  logger.info(`Querying messages for ticket ID: ${odooTicketId}`);
  const messages = await searchOdoo('mail.message', {
    domain: [
      [
        ['model', '=', 'helpdesk.ticket'],
        ['message_type', '=', 'email'],
        ['res_id', '=', ticketIdNumber],
      ],
    ],
    fields: ['id', 'email_from', 'body', 'create_date'],
    order: 'id desc',
  });

  if (messages && messages.length > 0) {
    const formattedMessages = messages.map(msg => {
      const messageType = msg.email_from?.includes('odoo')
        ? 'ODOO'
        : msg.email_from?.includes('source-werbe')
        ? 'SOURCE'
        : 'CLIENT';
      const e = new OdooMessageEntity({
        _id: A.getGloballyUniqueID(),
        odooMailMessageId: msg.id,
        odooInsertedAt: new Date(msg.create_date.replace(' ', 'T') + 'Z'),
        emailFrom: msg.email_from ?? null,
        body: msg.body ?? null,
        odooMessageType: messageType,
      });
      return {
        _id: e._id,
        odooMailMessageId: e.odooMailMessageId,
        odooInsertedAt: e.odooInsertedAt,
        emailFrom: e.emailFrom,
        body: e.body,
        odooMessageType: e.odooMessageType,
      };
    });

    // get all formattedMessages where odooMailMessageId is not in children
    const existingMessageIds = new Set(children.map(child => child.odooMailMessageId));
    const newMessages = formattedMessages.filter(msg => !existingMessageIds.has(msg.odooMailMessageId));

    logger.info(`Found ${newMessages.length} new messages for ticket ID: ${odooTicketId}`);

    const newAndExistingChildren = [...children, ...newMessages];

    await collection.updateOne(
      { odooTicketID: odooTicketId },
      { $set: { messages: newAndExistingChildren as unknown as OdooMessageEntity[] } },
    );
  }
}

export async function addMessagesToOdooEntity(
  odooTicketId: string,
  messages: Array<{ id: string; email_from: string; body: string; create_date: string }>,
): Promise<void> {
  const collection = await getCollection<OdooEntity>(OdooEntity._class);
  const ticket = await collection.findOne({ odooTicketID: odooTicketId });

  if (!ticket) {
    logger.warn(`No Odoo ticket found with ID: ${odooTicketId}`);
    return;
  }
  let children = ticket.messages || [];

  if (messages && messages.length > 0) {
    const formattedMessages = messages.map(msg => {
      const messageType = msg.email_from?.includes('odoo')
        ? 'ODOO'
        : msg.email_from?.includes('source-werbe')
        ? 'SOURCE'
        : msg.email_from?.includes('ndi-bot')
        ? 'BOT'
        : 'CLIENT';
      const e = new OdooMessageEntity({
        _id: A.getGloballyUniqueID(),
        odooMailMessageId: msg.id,
        odooInsertedAt: new Date(msg.create_date.replace(' ', 'T') + 'Z'),
        emailFrom: msg.email_from ?? null,
        body: msg.body ?? null,
        odooMessageType: messageType,
      });
      return e.toJSON();
    });

    // get all formattedMessages where odooMailMessageId is not in children
    const existingMessageIds = new Set(children.map(child => child.odooMailMessageId));
    const newMessages = formattedMessages.filter(msg => !existingMessageIds.has(msg.odooMailMessageId));

    logger.info(`Found ${newMessages.length} new messages for ticket ID: ${odooTicketId}`);

    const newAndExistingChildren = [...children, ...newMessages];

    await collection.updateOne(
      { odooTicketID: odooTicketId },
      { $set: { messages: newAndExistingChildren as unknown as OdooMessageEntity[] } },
    );
  }
}

export async function updateAllTicketsWithMissingMessages(): Promise<void> {
  const collection = await getCollection<OdooEntity>(OdooEntity._class);
  const allTickets = await collection.find({}).toArray();
}

export async function deleteOdooEntityByOdooTicketId(ticketId: string): Promise<any> {
  const collection = await getCollection<OdooEntity>(OdooEntity._class);
  return await collection.deleteOne({ odooTicketID: ticketId });
}

export async function updateOdooCreateDateByTicketId(ticketId: string, createDate: Date): Promise<void> {
  const collection = await getCollection<OdooEntity>(OdooEntity._class);
  await collection.updateOne(
    { odooTicketID: ticketId },
    { $set: { odooCreateDate: createDate, createdAt: createDate } },
  );
}

export async function ticketsWithTagAutoReply() {
  const collection = await getCollection<OdooEntity>(OdooEntity._class);
  const ticketsWithOdooTagsContainingAutoReply = await collection
    .find({
      odooTags: { $in: ['Auto-Reply'] },
    })
    .toArray();
  return ticketsWithOdooTagsContainingAutoReply.map(ticket => new OdooEntity(ticket));
}

@Entity()
export class CommentHelperEntity extends AdornisEntity {
  static override _class = 'CommentHelperEntity';

  static override resolveID(entity: CommentHelperEntity): string {
    return entity._id;
  }

  @Field(() => String)
  _id!: string;

  @Field(() => String)
  sourceID: Maybe<string>;

  @Field(() => String)
  odooTicketID: Maybe<string>;

  @Field(() => Comment)
  comment: Maybe<Comment>;

  @Field(() => Date)
  timestamp: Maybe<Date>;
}

export const getAllComments = registerQuery({
  operationName: 'getAllComments',
  type: () => [CommentHelperEntity],
  resolve: () => async (gqlFields: BaseQLSelectionSet<CommentHelperEntity>) => {
    const odooCollection = await getCollection<OdooEntity>(OdooEntity._class);
    const odooComments = await odooCollection
      .find({ comments: { $exists: true, $ne: null, $not: { $size: 0 } } })
      .toArray();
    const intercomCollection = await getCollection<IntercomEntity>(IntercomEntity._class);
    const intercomComments = await intercomCollection
      .find({ comments: { $exists: true, $ne: null, $not: { $size: 0 } } })
      .toArray();
    const contactCollection = await getCollection<ContactEntity>(ContactEntity._class);
    const contactComments = await contactCollection
      .find({ notes: { $exists: true, $ne: null, $not: { $size: 0 } } })
      .toArray();
    const commentArray: Array<CommentHelperEntity> = [];
    for (const odoo of odooComments) {
      commentArray.push(
        new CommentHelperEntity({
          source: 'ODOO',
          id: odoo.odooTicketID,
          comments: odoo.comments ?? [],
        }),
      );
    }
    for (const intercom of intercomComments) {
      commentArray.push(
        new CommentHelperEntity({
          source: 'INTERCOM',
          id: intercom.intercomId,
          comments: intercom.comments ?? [],
        }),
      );
    }
    for (const contact of contactComments) {
      commentArray.push(
        new CommentHelperEntity({
          source: 'CONTACT',
          id: contact._id,
          comments: contact.notes ?? [],
        }),
      );
    }
    return commentArray;
  },
});
